import { useEffect, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { DateFormat, EntityType } from '@equip.health/ui';
import { AppointmentStatus } from '~/lib/constants/calendar.constants';

import {
  useGetPatientCoveragesQuery,
  useLazyGetDuplicateInquiryCoveragesQuery,
  useGetProviderTeamAssignmentsQuery,
} from '~/store/services/clinicalData';
import { useGetInquiryDetailByIdQuery } from '~/store/services/inquiry';
import { useLazyGetPatientInquiryNotesQuery } from '~/store/services/notes/notes';
import {
  useLazyGetDuplicateContactsQuery,
  useGetPatientContactsByInquiryIdQuery,
  useGetPatientQuery,
  useLazyGetPatientMatchesQuery,
} from '~/store/services/patients';
import { useGetTaskSummariesQuery } from '~/store/services/task';
import { useGetAppointmentDetailsQuery } from '~/store/services/appointment';
import { normalizeDuplicateResponseData } from '~/lib/util/inquiry.util';
import { RelationToPatientCategory } from '~/lib/constants/enumeration.constants';
import { useGetRelationsToPatientEnumQuery } from '~/store/services/enumerations';
import {
  InquiryStatusValue,
  NormalizeDuplicateResponseData,
} from '~/lib/constants/inquiry.constants';
import { PatientCoverage } from '~/lib/constants/patients.constants';
import { isAssignmentActive } from '~/lib/util/careTeam.util';
import {
  GetProvidersResponseV2,
  useGetAllProvidersV2Query,
} from '~/store/services/providers';

export type InquiryDetailState = {
  inquiry?: InquiryDetailResponse;
  notes?: GetPatientInquiryNotesResponse;
  coverages: PatientCoverage[];
  duplicateContacts?: GetDuplicateContactsResponse;
  duplicateCoverages?: GetDuplicateCoveragesResponse;
  appointments?: GetAppointmentDetailsResponse;
  assignedCareTeamProviders: GetProviderByIdResponse[];
  patient: Patient;
  contacts: PatientContact[];
  patientMatches: GetPatientMatches;
  tasks: TaskSummaryResponse;
  users?: UserDetails[];
  providers?: GetProvidersResponseV2;
  fetchInquiryLoading: boolean;
  fetchAppointmentsLoading: boolean;
  fetchContactsLoading: boolean;
  fetchCoveragesLoading: boolean;
  fetchDuplicateCoverages: (
    patientId: string,
  ) => Promise<NormalizeDuplicateResponseData[]>;
  fetchDuplicateCoveragesLoading: boolean;
  fetchDuplicateContacts: (
    patientId: string,
  ) => Promise<NormalizeDuplicateResponseData[]>;
  fetchDuplicateContactsLoading?: boolean;
  fetchNotesLoading: boolean;
  fetchPatientLoading: boolean;
  fetchPatientMatchesLoading: boolean;
  fetchTasksLoading: boolean;
  fetchProvidersLoading: boolean;
  isLoading: boolean;
  isPatientMatchConfirmed: boolean;
  loadMoreNotes: () => void;
};

const NOTES_PAGE_SIZE = 10;

/**
 * A hook that makes all required API calls to populate the inquiry detail drawer.
 * @param inquiryId The inquiry id.
 */
export const useInquiryDetail = (inquiryId: string): InquiryDetailState => {
  const defaultFetchOptions = {
    // We want to reload inquiry every time an arg changes to ensure that we have the latest version of the inquiry.
    refetchOnMountOrArgChange: true,
    // Don't fetch if inquiry id is not set.
    skip: !inquiryId,
  };
  const [notesPageNumber, setNotesPageNumber] = useState<number>(1);

  const { data: inquiry, isFetching: fetchInquiryLoading } =
    useGetInquiryDetailByIdQuery(inquiryId, defaultFetchOptions);

  const patientId = inquiry?.patient?.id;
  const shouldFetchProviderTeamAssignments =
    !!patientId &&
    [InquiryStatusValue.ONBOARDING, InquiryStatusValue.ENROLLED].includes(
      inquiry?.status?.name as InquiryStatusValue,
    );
  const {
    data: providerTeamAssignments,
    isFetching: fetchProviderTeamAssignmentsLoading,
  } = useGetProviderTeamAssignmentsQuery(patientId ?? '', {
    skip: !shouldFetchProviderTeamAssignments,
  });

  const [
    getPatientInquiryNotes,
    { data: notes, isFetching: fetchNotesLoading },
  ] = useLazyGetPatientInquiryNotesQuery();

  const [
    getDuplicateContacts,
    { data: duplicateContacts, isFetching: fetchDuplicateContactsLoading },
  ] = useLazyGetDuplicateContactsQuery();

  const [
    getDuplicateCoverages,
    { data: duplicateCoverages, isFetching: fetchDuplicateCoveragesLoading },
  ] = useLazyGetDuplicateInquiryCoveragesQuery();

  useEffect(() => {
    if (inquiryId) {
      getPatientInquiryNotes({
        inquiryId,
        notesPageNumber,
        notesPageSize: NOTES_PAGE_SIZE,
      });
    }
  }, [inquiryId, notesPageNumber, getPatientInquiryNotes]);

  const loadMoreNotes = (): void => {
    const newNotesPageNumber = notesPageNumber + 1;
    setNotesPageNumber(newNotesPageNumber);
    getPatientInquiryNotes({
      inquiryId,
      notesPageNumber: newNotesPageNumber,
      notesPageSize: NOTES_PAGE_SIZE,
    });
  };
  const { data: relationsToPatientInContact } =
    useGetRelationsToPatientEnumQuery();
  const familyRelations = useMemo<string[]>(
    () =>
      (relationsToPatientInContact || [])
        .filter((x) => x.category === RelationToPatientCategory.FAMILY)
        .map((x) => x.label)
        .sort((a, b) => a.localeCompare(b)),
    [relationsToPatientInContact],
  );

  const getCareTeamProviders = (
    assignments: ProviderTeamPreliminaryAssignment[] | ProviderTeamAssignment[],
    providersMap: Record<string, GetProviderByIdResponse>,
  ) =>
    assignments
      .map(({ providerId }) => providersMap[providerId])
      .filter(Boolean);

  const fetchDuplicateContacts = async (patientId: string) => {
    const res = await getDuplicateContacts({
      inquiryId,
      patientId: patientId ?? inquiry?.patient?.id,
    });
    if (res?.data) {
      return normalizeDuplicateResponseData(
        res.data,
        EntityType.CONTACT,
        familyRelations,
      );
    }
    return null;
  };

  const fetchDuplicateCoverages = async (patientId: string) => {
    const res = await getDuplicateCoverages({
      coverageDate: DateTime.fromJSDate(new Date(inquiry.createdAt)).toFormat(
        DateFormat.shortYearMonthDay,
      ),
      inquiryId,
      patientId: patientId ?? inquiry?.patient?.id,
    });
    if (res?.data) {
      return normalizeDuplicateResponseData(res.data, EntityType.COVERAGE);
    }
    return null;
  };

  const { data: coverages, isFetching: fetchCoveragesLoading } =
    useGetPatientCoveragesQuery(
      { inquiryId, patientId },
      { ...defaultFetchOptions, skip: !inquiryId || !patientId },
    );

  const { data: appointments, isFetching: fetchAppointmentsLoading } =
    useGetAppointmentDetailsQuery(
      {
        inquiryIds: [inquiryId],
        pageSize: 50,
        statuses: [AppointmentStatus.SCHEDULED],
      },
      defaultFetchOptions,
    );

  const { data: patient, isFetching: fetchPatientLoading } = useGetPatientQuery(
    { patientId },
    { ...defaultFetchOptions, skip: !patientId },
  );
  const { data: contactsResponse, isFetching: fetchContactsLoading } =
    useGetPatientContactsByInquiryIdQuery(inquiryId, defaultFetchOptions);

  const userIds = useMemo<string[]>(() => {
    const ids = new Set<string>();

    // Include admissions team user IDs
    inquiry?.assignments?.forEach((x) => ids.add(x.userId));

    // Include note author IDs
    notes?.data?.forEach((x) => ids.add(x.createdBy));

    // Include provider team assignment provider IDs so they resolve in providersMap
    const inquiryAssignments =
      providerTeamAssignments?.inquiry?.providerTeamAssignments ?? [];
    const episodeAssignments =
      providerTeamAssignments?.treatmentEpisode?.providerTeamAssignments ?? [];
    [...inquiryAssignments, ...episodeAssignments].forEach((a) =>
      ids.add(a.providerId),
    );

    return [...ids];
  }, [inquiry?.assignments, notes?.data, providerTeamAssignments]);

  const { data: providersResponse, isFetching: fetchProvidersLoading } =
    useGetAllProvidersV2Query(
      {
        id: userIds,
        'include-out-of-office': true,
        'include-provider-history': true,
        'page-number': 1,
        'page-size': userIds?.length,
      },
      {
        skip: userIds.length === 0,
      },
    );

  const providersMap: Record<string, GetProviderByIdResponse> = useMemo(() => {
    if (!providersResponse?.data) return {};
    return providersResponse.data.reduce(
      (acc, provider) => {
        acc[provider.userId] = provider;
        return acc;
      },
      {} as Record<string, GetProviderByIdResponse>,
    );
  }, [providersResponse]);

  const assignedCareTeamProviders: GetProviderByIdResponse[] = useMemo(() => {
    if (!providerTeamAssignments) return [];
    switch (inquiry?.status?.name) {
      case InquiryStatusValue.ONBOARDING:
        return getCareTeamProviders(
          providerTeamAssignments?.inquiry?.providerTeamAssignments ?? [],
          providersMap,
        );
      case InquiryStatusValue.ENROLLED: {
        const treatmentEpisodeAssignments = (
          providerTeamAssignments?.treatmentEpisode?.providerTeamAssignments ??
          []
        ).filter(isAssignmentActive);
        const inquiryAssignments =
          providerTeamAssignments?.inquiry?.providerTeamAssignments ?? [];

        // During status transition, if treatmentEpisode data hasn't loaded yet
        // but inquiry data exists, temporarily show inquiry data to prevent flickering
        if (
          treatmentEpisodeAssignments.length === 0 &&
          inquiryAssignments.length > 0
        ) {
          return getCareTeamProviders(inquiryAssignments, providersMap);
        }
        return getCareTeamProviders(treatmentEpisodeAssignments, providersMap);
      }
      default:
        return [];
    }
  }, [inquiry?.status?.name, providerTeamAssignments, providersMap]);

  const { data: tasks, isFetching: fetchTasksLoading } =
    useGetTaskSummariesQuery(
      { inquiryId, pageSize: 25 },
      { ...defaultFetchOptions, skip: !inquiryId },
    );

  const [
    getPatientMatches,
    { data: patientMatches, isFetching: fetchPatientMatchesLoading },
  ] = useLazyGetPatientMatchesQuery();

  const isPatientMatchConfirmed = !!inquiry?.patientMatchConfirmedAt;

  useEffect(() => {
    if (
      inquiry?.id &&
      !inquiry?.patientMatchConfirmedAt &&
      patient &&
      contactsResponse?.data
    ) {
      const primaryContact = contactsResponse?.data?.find(
        (c) => c.isPrimaryContact,
      );

      const contactPhoneNumber =
        patient.phoneNumber ??
        patient.secondaryPhoneNumber ??
        patient.secondaryCellPhone ??
        patient.homePhone ??
        patient.officeNumber;

      getPatientMatches({
        contactEmail: patient.email,
        contactPhoneNumber,
        patientDateOfBirth: patient.dateOfBirth,
        patientEmail: patient.email,
        patientFirstName: patient.firstName,
        patientLastName: patient.lastName,
        patientPhoneNumber: patient.phoneNumber,
        relationWithPatient: primaryContact?.relationToPatient,
        supportFirstName: primaryContact?.firstName,
        supportLastName: primaryContact?.lastName,
      });
    }
  }, [
    inquiry?.id,
    inquiry?.patientMatchConfirmedAt,
    patient,
    contactsResponse,
    getPatientMatches,
  ]);

  const isLoading =
    fetchInquiryLoading ||
    fetchAppointmentsLoading ||
    fetchContactsLoading ||
    fetchCoveragesLoading ||
    fetchDuplicateContactsLoading ||
    fetchNotesLoading ||
    fetchPatientLoading ||
    fetchPatientMatchesLoading ||
    fetchProvidersLoading ||
    fetchTasksLoading ||
    fetchProviderTeamAssignmentsLoading;

  return {
    appointments,
    assignedCareTeamProviders,
    contacts: contactsResponse?.data,
    coverages,
    duplicateContacts,
    duplicateCoverages,
    fetchAppointmentsLoading,
    fetchContactsLoading,
    fetchCoveragesLoading,
    fetchDuplicateContacts,
    fetchDuplicateContactsLoading,
    fetchDuplicateCoverages,
    fetchDuplicateCoveragesLoading,
    fetchInquiryLoading,
    fetchNotesLoading,
    fetchPatientLoading,
    fetchPatientMatchesLoading,
    fetchProvidersLoading,
    fetchTasksLoading,
    inquiry,
    isLoading,
    isPatientMatchConfirmed,
    loadMoreNotes,
    notes,
    patient,
    patientMatches,
    providers: providersResponse,
    tasks,
  };
};
